import {
  colorBackgroundAlt,
  colorBackgroundAltInverse,
  colorBaseNeutralWhite,
} from '@duxton-web/design-tokens/themes/light';
import { ArrowLeftUniversal } from '@duxton-web/icons';
import { Button, Divider, Flex, Form, Spin, Tooltip, Typography } from '@duxton-web/ui';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid4 } from 'uuid';

import { convertHoursInUTCToTimeWindow } from 'omega-rtc/utils/time-utils';
import { getFeatureFlagConfig } from 'actions/newTridentExtra';
import {
  clearMystiqueContent,
  CREATE_UCD_CAMPAIGN,
  cpListCvps,
  cpListProducts,
  createUCDCampaign,
  CP_LIST_PRODUCTS,
  GET_UCD_CAMPAIGN,
  getUCDCampaign,
  getUCDConfigs,
  getUCDProject,
  resetNewlyCreatedCampaign,
  resetUCDCampaign,
  UCD_GENERATE_MYSTIQUE_CONTENT,
  UCD_GENERATE_MYSTIQUE_CONTENT_FEEDBACK,
  UPDATE_UCD_CAMPAIGN,
  UPDATE_UCD_CAMPAIGN_STATUS,
  updateUCDCampaign,
  updateUCDCampaignStatus,
} from 'omega-rtc/actions/ucd';
import CreateSmartCampaignProvider, {
  useCreateSmartCampaign,
} from 'omega-rtc/components/container/CreateSmartCampaignProvider';

// Import proper types
type MystiqueRequestMetaData = {
  contents: string[];
  otherMetaData?: {
    primaryLanguage: string;
    isUserEditModeActiveForMystiqueInputs: boolean;
    requestCount?: number;
  };
  requestID?: string;
  isSlangModeEnabled: boolean;
  isRequestPending: boolean;
  isReGenerateMode: boolean;
  previousLanguage?: string;
};

type PrimaryLanguageContents = {
  lastVariantNumber: number;
  lastRoundNumber: number;
  selectedControlGroupVariant?: string;
  rounds: Record<string, any>;
  mode: string;
};


import CustomizeRequiredMark from 'omega-rtc/components/custom/CustomizeRequiredMark';
import { snackBar } from 'omega-rtc/components/custom/SnackBar';
import SmartCampaignCreateEdit from 'omega-rtc/components/form/ucd/SmartCampaignCreateEdit';
import SmartCampaignPCIMetadata from 'omega-rtc/components/form/ucd/SmartCampaignPCIMetadata';
import SmartCampaignPreview from 'omega-rtc/components/form/ucd/SmartCampaignPreview';
import SmartCommsUnsavedChangesConfirmationModal from 'omega-rtc/components/form/ucd/SmartCommsUnsavedChangesConfirmationModal';
import { showControlGroup } from 'omega-rtc/config';
import {
  channelOptions,
  COUNTRY_ID_VS_COUNTRY_CODE,
  LANGUAGE_CODE_BASED_ON_COUNTRY_ID,
  MIN_SELECTED_VARIANTS_COUNT_IN_SMART_CAMPAIGN,
  QUERY_PARAM_KEYS,
  SMART_CAMPAIGN_CHANNELS,
  SMART_CAMPAIGN_CONTENT_SOURCE_MODES,
  SMART_CAMPAIGN_FORM_KEYS,
  SMART_CAMPAIGN_MYSTIQUE_CONTENT_NUM,
  UCD_ROUTES_LINK,
} from 'omega-rtc/constants/ucd';
import { SmartCampaignStatus, UcdCampaignResourceName, UcdUniversalScope } from 'omega-rtc/data/trident-campaigns';
import {
  isRequestsPending,
  isRequestsSuccessful,
} from 'omega-rtc/utils/request-state-utils';
import {
  fillContentContainerFormValues,
  getRequiredFieldsStatus,
  getSmartCampaignPayload,
  getValidMystiqueContent,
  isContentPortalProductsEnabled as getIsContentPortalProductsEnabled,
  isMetadataInputFormFilled,
  isXCOEnabled,
  mapAttributesValuesToLabelsInMystiqueResponse,
  sendScribeEventForMystiqueContentGeneration,
  transformContentVariantsInCampaignResponse,
  validateMandatoryFieldsForCreateOrUpdate,
} from 'omega-rtc/utils/smart-campaign-utils';
import {
  getRequestStateKey,
  getSmartCommsRoundNameFromNumber,
  getSmartCommsVariantNameFromNumber,
} from 'omega-rtc/utils/ucd-utils';
import {
  isTaiwanCampaignCreationAllowed,
  TAIWAN_CAMPAIGN_CREATION_RESTRICTED_MESSAGE,
} from 'omega-rtc/utils/taiwan-campaign-operation';
import {
  countChosenObjectsAndValidateTitleContent,
  validateSmartCommsCampaignPayload,
} from 'omega-rtc/utils/ucd-validate-payloads';
import { getValueByFeatureFlag, hasPermission, hasTridentPermission } from 'omega-rtc/utils/user-utils';
import { STATUS_ACTIVE } from 'omega-rtc/views/ucd/content-config/adapters';

dayjs.extend(timezone);

const DEFAULT_CONTENT_VIEW = 0;
const EMPTY_ARRAY: any[] = [];

const VIEW_TYPES = {
  METADATA_INPUT_FORM: 'METADATA_INPUT_FORM',
  PCI_METADATA_FORM: 'PCI_METADATA_FORM',
  PREVIEW: 'PREVIEW',
};

// Show snackBar instead of toastr, on error from API Middleware

const CAMPAIGN_STATE = {
  draft: 'SAVE_AS_DRAFT',
  approval: 'SUBMIT_FOR_APPROVAL',
  updateToPendingStatus: 'UPDATE_TO_PENDING_STATUS',
};

type UCDCreateSmartCommsCampaignProps = any;

const UCDCreateSmartCommsCampaign = (props: UCDCreateSmartCommsCampaignProps) => {
  const [form] = Form.useForm();

  return (
    <CreateSmartCampaignProvider form={form}>
      <CreateEditContainer props={props} form={form} />
    </CreateSmartCampaignProvider>
  );
};

type CreateEditContainerProps = {
  props: any;
  form: any;
};

const CreateEditContainer = ({ props, form }: CreateEditContainerProps) => {
  const dispatch = useDispatch();
  const isCreateSmartCampaignMode = useMemo(
    () => props.route.path === UCD_ROUTES_LINK.SMART_CAMPAIGN_CREATE(),
    [props.route],
  );
  const {
    isCampaignLoading,
    selectedLanguages,
    callToActionOptions,
    isSaveCampaignLoading,
    isMystiqueCallPending,
    mystiqueRequestMetaData,
    isContentVariantsTouched,
    imageDataForEachLanguage,
    primaryLanguageContentsInForm,
    numberOfSelectedVariants,

    setMystiqueRequestMetaData,
    setSelectedLanguages,
    setIsCampaignLoading,
    setCallToActionOptions,
    setIsSaveCampaignLoading,
    setImageDataForEachLanguage,
    setIsContentVariantsTouched,
    setPrimaryLanguageContentsInForm,

    generateMystiqueContents,
    clearMystiqueRequestMetaData,
  } = useCreateSmartCampaign();

  const isPCIFormat = useMemo(() => {
    return (
      form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CHANNEL) ===
      SMART_CAMPAIGN_CHANNELS.pci
    );
  }, [form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CHANNEL)]);
  const { campaignToDuplicateId, isDuplicateSmartCampaignMode, shouldCopyContent } =
    useMemo(() => {
      const campaignToDuplicate =
        props.location.query && props.location.query.campaignToDuplicate;
      const copyContent = props.location.query && props.location.query.copyContent === 'true';

      // Check if the current path matches the base duplicate path
      const baseUrl = UCD_ROUTES_LINK.SMART_CAMPAIGN_DUPLICATE(
        props.params.entityId,
        campaignToDuplicate,
      );
      const currentPathWithoutCopyContent = props.location.pathname +
        (campaignToDuplicate ? `?${QUERY_PARAM_KEYS.campaignToDuplicate}=${campaignToDuplicate}` : '');

      const isDuplicateSmartCampaignMode = campaignToDuplicate && currentPathWithoutCopyContent === baseUrl;

      return {
        campaignToDuplicateId: campaignToDuplicate,
        isDuplicateSmartCampaignMode,
        shouldCopyContent: copyContent,
      };
    }, [props.route]);

  const isEditSmartCampaignMode = useMemo(() => {
    return (
      props.location.pathname ===
      UCD_ROUTES_LINK.SMART_CAMPAIGN_EDIT(
        props.params.entityId,
        props.params.campaignId,
      )
    );
  }, []);

  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(true);
  const [currentContentViewIndex, setCurrentContentViewIndex] =
    useState(DEFAULT_CONTENT_VIEW);
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] =
    useState(false);
  const [campaignSubmitMode, setCampaignSubmitMode] = useState('');

  // Watch form fields to trigger re-renders when values change (for tooltip updates)
  // eslint-disable-next-line no-unused-vars
  const watchCampaignName = Form.useWatch(SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_NAME, form);
  const watchSegments = Form.useWatch(SMART_CAMPAIGN_FORM_KEYS.SEGMENTS, form);

  const configs = useSelector((state: any) => state.ucd.configs);
  const campaignView = useSelector((state: any) => state.ucd.campaignView);
  const newlyCreatedCampaign = useSelector(
    (state: any) => state.ucd.newlyCreatedCampaign,
  );
  const request = useSelector((state: any) => state.requests);
  const mystiqueContent = useSelector((state: any) => state.ucd.mystiqueContent);
  const mystiqueRequestTime = useSelector(
    (state: any) => state.ucd.mystiqueRequestTime,
  );
  const countries = useSelector((state: any) => state.countries.entities);

  const projectView = useSelector((state: any) => state.ucd.projectView);
  const project = projectView && projectView.project;

  const countryId = project && project.countryID;
  const contentPortalCountryId = countryId || (campaignView && campaignView.countryID);
  const country =
    countries && countries.find((country: any) => country.countryID === countryId);
  const countryCode =
    (contentPortalCountryId && COUNTRY_ID_VS_COUNTRY_CODE[contentPortalCountryId]) || '';
  const shouldUseContentPortalProducts = getIsContentPortalProductsEnabled();
  const contentPortalProducts = useSelector(
    (state: any) => _.get(state, `ucd.cpProductsByCountry.${countryCode}.active`, EMPTY_ARRAY),
  );
  const hasContentPortalProductsLoaded = useSelector(
    (state: any) => Boolean(
      countryCode &&
      _.has(state, ['ucd', 'cpProductsByCountry', countryCode, 'active']),
    ),
  );

  const user = useSelector((state: any) => state.app.user);
  const featureFlagConfig = useSelector((state: any) => state.newTridents.featureFlagConfig);
  const isHolosPermissionEnabled = _.get(featureFlagConfig, 'holosPortalPermissionEnbaled', false); // TODO: remove this after holos portal permission is enabled for all users
  const mystiqueRequestPayload = useSelector(
    (state: any) => state.ucd.mystiqueRequestPayload,
  );

  const hasEditPermission = useMemo(() => {
    return (
      campaignView &&
      campaignView.status === SmartCampaignStatus.draft &&
      getValueByFeatureFlag(
        isHolosPermissionEnabled,
        hasPermission(user.userPermissions, UcdCampaignResourceName, UcdUniversalScope, 'update'),
        hasTridentPermission(user.userPermissions, '', '', 'update')
      )
    );
  }, [user, campaignView]);

  const {
    campaignRequest,
    mystiqueContentRequest,
    mystiqueRegenerateContentRequest,
    createCampaignRequest,
    updateCampaignRequest,
    updateCampaignStatusRequest,
    contentPortalProductsRequest,
  } = useMemo(() => {
    return {
      campaignRequest: _.get(request, getRequestStateKey(GET_UCD_CAMPAIGN), {}),
      mystiqueContentRequest: _.get(
        request,
        getRequestStateKey(UCD_GENERATE_MYSTIQUE_CONTENT),
        {},
      ),
      mystiqueRegenerateContentRequest: _.get(
        request,
        getRequestStateKey(UCD_GENERATE_MYSTIQUE_CONTENT_FEEDBACK),
        {},
      ),
      createCampaignRequest: _.get(
        request,
        getRequestStateKey(CREATE_UCD_CAMPAIGN),
      ),
      updateCampaignRequest: _.get(
        request,
        getRequestStateKey(UPDATE_UCD_CAMPAIGN),
      ),
      updateCampaignStatusRequest: _.get(
        request,
        getRequestStateKey(UPDATE_UCD_CAMPAIGN_STATUS),
      ),
      contentPortalProductsRequest: _.get(
        request,
        getRequestStateKey(CP_LIST_PRODUCTS),
        {},
      ),
    };
  }, [request]);
  const hasContentPortalProductsLoadFailed = Boolean(
    contentPortalProductsRequest.isFailure,
  );

  useEffect(() => {
    dispatch(getUCDConfigs());
    dispatch(resetNewlyCreatedCampaign());
    dispatch(getFeatureFlagConfig());
    props.triggerFullView(true);

    if (isCreateSmartCampaignMode) {
      window.addEventListener('popstate', handlePopState);
      window.history.pushState({ modalOpened: false }, '', props.route.path);
    }

    return () => {
      dispatch(clearMystiqueContent());
      dispatch(resetUCDCampaign());
      props.triggerFullView(false);
      if (isCreateSmartCampaignMode) {
        window.removeEventListener('popstate', handlePopState);
      }
    };
  }, []);

  useEffect(() => {
    if (!shouldUseContentPortalProducts || !countryCode) {
      return;
    }

    dispatch(cpListProducts(countryCode, STATUS_ACTIVE));
    dispatch(cpListCvps(countryCode, STATUS_ACTIVE));
  }, [shouldUseContentPortalProducts, countryCode]);

  useEffect(() => {
    if (_.isEmpty(mystiqueContent)) {
      return;
    }
    const chosenChannel = form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CHANNEL);

    const mystiqueChannel = channelOptions.find(
      (channel) => channel.value === chosenChannel,
    ).mystiqueContentType;

    const requestCount = mystiqueRequestMetaData && mystiqueRequestMetaData.otherMetaData
      ? mystiqueRequestMetaData.otherMetaData.requestCount || SMART_CAMPAIGN_MYSTIQUE_CONTENT_NUM
      : SMART_CAMPAIGN_MYSTIQUE_CONTENT_NUM;

    const generatedContents = getValidMystiqueContent({
      mystiqueContents: mystiqueContent,
      mystiqueChannel,
      allowPartialContentCount: Boolean(_.get(mystiqueRequestPayload, 'base_config_version')),
      numberOfContentsRequested: requestCount,
    });

    if (!generatedContents) {
      snackBar.warning({ content: 'Failed to generate. Please try again.' });
      return;
    }

    /**
     *  Normal request
     */
    if (!mystiqueRequestMetaData || !mystiqueRequestMetaData.isSlangModeEnabled) {
      handleMystiqueResponse(Object.values(generatedContents));
      clearMystiqueRequestMetaData();
      return;
    }

    /**
     * slang enabled requests
     *
     * We will have two mystique calls if slangMode is ON
     * 1. First call with primary language
     * 2. Second call with slang based language
     *
     * mystiqueRequestMetaData.previousLanguage will store language which is used for current request.
     */
    const typedMystiqueRequestMetaData = mystiqueRequestMetaData;
    const isContentGeneratedForSlang =
      typedMystiqueRequestMetaData && typedMystiqueRequestMetaData.previousLanguage !==
      (typedMystiqueRequestMetaData.otherMetaData && typedMystiqueRequestMetaData.otherMetaData.primaryLanguage); // mystiqueRequestMetaData.previousLanguage is not equal to primary language then, language in mystique payload will be based on slang.

    if (isContentGeneratedForSlang) {
      const slang = typedMystiqueRequestMetaData && typedMystiqueRequestMetaData.previousLanguage;
      const newContents = Object.values(generatedContents).map((value: any) => ({
        ...value,
        slang,
      }));

      handleMystiqueResponse([
        ...(typedMystiqueRequestMetaData && typedMystiqueRequestMetaData.contents || []),
        ...newContents,
      ]);
      clearMystiqueRequestMetaData();
      return;
    }

    if (!isContentGeneratedForSlang) {
      const newContents = Object.values(generatedContents);
      setMystiqueRequestMetaData((prev: MystiqueRequestMetaData) => ({
        ...prev,
        contents: newContents,
      }));
      generateMystiqueContents({
        ...(typedMystiqueRequestMetaData && typedMystiqueRequestMetaData.otherMetaData || {}),
        previousRequestID: typedMystiqueRequestMetaData && typedMystiqueRequestMetaData.requestID,
      });
    }
  }, [mystiqueContent]);

  useEffect(() => {
    /**
     * If campaign is in duplicate mode,
     * then, we need to set primary language from 'projectView' (project details) - only at the initial state.
     * NOTE: If content variants are already generated then, we don't need to update primary language since, that logic is done smartCampaignMetaData.jsx
     */
    if (
      !project ||
      !isDuplicateSmartCampaignMode ||
      primaryLanguageContentsInForm.lastRoundNumber > 0
    ) {
      return;
    }

    const entityId = props.params.entityId;

    if (countryId && projectView.projectID === entityId) {
      const defaultLanguageBasedOnCountryId =
        LANGUAGE_CODE_BASED_ON_COUNTRY_ID[countryId];
      setSelectedLanguages([defaultLanguageBasedOnCountryId]);
    }
  }, [isDuplicateSmartCampaignMode, projectView, form.getFieldValue]);

  useEffect(() => {
    if (campaignView && isEditSmartCampaignMode && !hasEditPermission) {
      props.router.replace(
        UCD_ROUTES_LINK.SMART_CAMPAIGN_VIEW(
          campaignView.projectID,
          campaignView.campaignID,
        ),
      );
    }

    // Removing unnecessary values from campaignView, If campaign is in duplicate mode
    if (
      campaignView &&
      isDuplicateSmartCampaignMode &&
      campaignToDuplicateId === campaignView.campaignID
    ) {
      campaignView.campaign.name = `Copy_${campaignView.campaign.name.replaceAll(' ', '_')}`;
      campaignView.campaign.approver = undefined;
      campaignView.status = undefined;
      campaignView.campaign.code = undefined;

      if (!shouldCopyContent) {
        campaignView.campaign.smartCommsContentMetadata.contents = [];
      } else if (
        campaignView.campaign.smartCommsContentMetadata &&
        campaignView.campaign.smartCommsContentMetadata.contents &&
        campaignView.campaign.smartCommsContentMetadata.contents.length
      ) {
        // Process content variants when copying content
        campaignView.campaign.smartCommsContentMetadata.contents = constructContentVariants(
          campaignView.campaign.smartCommsContentMetadata.contents
        );
      }
      // Always stay on metadata input form (index 0) when duplicating
      setCurrentContentViewIndex(0);
    }

    const currentCampaignID = props.params.campaignId || campaignToDuplicateId;
    if (campaignView && currentCampaignID === campaignView.campaignID) {
      const { campaign } = campaignView;

      const timezones = country && country.timezones;
      const controlGroupProjectTime = dayjs.tz(
        dayjs(campaign.controlGroupTime),
        timezones && timezones[0] && timezones[0].tz,
      );

      const timezoneOffset = timezones && timezones[0] ? timezones[0].offset : 0;
      const timeWindowSegments = convertHoursInUTCToTimeWindow(campaign.hoursInUTC, timezoneOffset);

      const smartCommsMetadata = campaign.smartCommsMetadata;
      let customRecurrenceWindowInDays: number | undefined;
      if (smartCommsMetadata && smartCommsMetadata.customRecurrenceWindowInDays > 0) {
        customRecurrenceWindowInDays = smartCommsMetadata.customRecurrenceWindowInDays;
      } else if (smartCommsMetadata && smartCommsMetadata.isRecurrenceEnabled) {
        customRecurrenceWindowInDays = 3;
      }

      form.setFieldsValue({
        campaignName: campaign.name,
        startDate: dayjs(campaign.start),
        endDate: dayjs(campaign.end),
        controlGroupTime: controlGroupProjectTime,
        project: props.params.entityId,
        segments: campaign.targetSegments ? campaign.targetSegments[0] : '',
        vertical: campaign.verticals ? campaign.verticals[0] : '',
        cvp: campaign.verticals ? campaign.CVP : '',
        approver: campaign.approver,
        channel: campaign.channels ? campaign.channels[0] : '',
        segmentSize:
          campaign.smartCommsMetadata &&
          campaign.smartCommsMetadata.segmentSize,
        isRecurrenceEnabled: (campaign.smartCommsMetadata && campaign.smartCommsMetadata.isRecurrenceEnabled) || false,
        customRecurrenceWindowInDays,
        timeWindow: timeWindowSegments && timeWindowSegments[0] ? timeWindowSegments[0] : null, // TODO: Update the logic to handle multiple time window segments
        isTimeSensitive: timeWindowSegments && timeWindowSegments[0],
      });
      const contentVariants = countChosenObjectsAndValidateTitleContent(
        campaign.smartCommsContentMetadata,
      );
      setIsCampaignLoading(false);

      // Only auto-navigate if not in duplicate mode
      if (!isDuplicateSmartCampaignMode) {
        const isPCIChannel =
          form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CHANNEL) ===
          SMART_CAMPAIGN_CHANNELS.pci;
        const isPrimaryInputFormFilled = isMetadataInputFormFilled({
          formInstance: form,
          shouldUseContentPortalProducts,
          contentPortalProducts,
          hasContentPortalProductsLoaded,
          hasContentPortalProductsLoadFailed,
        });
        const isPCIFieldsFilled = isPCIChannel
          ? isMetadataInputFormFilled({
            formInstance: form,
            isPrimaryInputFormViewActive: false,
          })
          : true;
        if (
          isPrimaryInputFormFilled &&
          contentVariants.isControlledGroupSelected &&
          contentVariants.count >= MIN_SELECTED_VARIANTS_COUNT_IN_SMART_CAMPAIGN
        ) {
          const previewIndex = isPCIChannel ? 2 : 1;
          const nextViewIndex = isPCIFieldsFilled ? previewIndex : 1;
          setCurrentContentViewIndex(nextViewIndex);
        }
      }
    }
  }, [campaignView, props.params]);

  useEffect(() => {
    if (
      mystiqueContentRequest.isFailure ||
      mystiqueRegenerateContentRequest.isFailure
    ) {
      setMystiqueRequestMetaData((prev: MystiqueRequestMetaData) => ({
        ...prev,
        isRequestPending: !(
          (mystiqueRegenerateContentRequest.isFailure &&
            prev.isReGenerateMode) ||
          (mystiqueContentRequest.isFailure && !prev.isReGenerateMode)
        ),
      }));
    }
  }, [mystiqueContentRequest, mystiqueRegenerateContentRequest]);

  useEffect(() => {
    const isPending = isRequestsPending(campaignRequest);

    if (isPending) {
      setIsCampaignLoading(true);
      return;
    }

    const isCampaignRequestFailed = campaignRequest.isFailure;
    if (isCampaignRequestFailed) {
      setIsCampaignLoading(false);
    }

    // After data is set to form, loading state will change to false if request is success.
  }, [campaignView, campaignRequest]);

  useEffect(() => {
    const currentCampaignID = props.params.campaignId || campaignToDuplicateId;
    if (props.params.entityId && currentCampaignID) {
      dispatch(
        getUCDCampaign(
          props.params.entityId,
          currentCampaignID,
        ),
      );
      dispatch(
        getUCDProject(props.params.entityId),
      );
    }
  }, [props.params]);

  const campaignID = isDuplicateSmartCampaignMode
    ? newlyCreatedCampaign
    : (campaignView && campaignView.campaignID) || newlyCreatedCampaign;

  const handleSuccessfulCampaignRequest = () => {
    if (campaignSubmitMode === CAMPAIGN_STATE.approval && campaignID) {
      setCampaignSubmitMode(CAMPAIGN_STATE.updateToPendingStatus);
      dispatch(
        updateUCDCampaignStatus(
          SmartCampaignStatus.pending,
          user.email,
          form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PROJECT),
          campaignID,
        ),
      );
      // TODO: add scribe event
    }
    if (campaignSubmitMode === CAMPAIGN_STATE.draft) {
      snackBar.success({
        content: 'Progress saved successfully! 🎉',
      });
      if (isUnsavedChangesModalOpen && campaignID) {
        setIsUnsavedChangesModalOpen(false);
        props.router.push(
          UCD_ROUTES_LINK.SMART_CAMPAIGN_VIEW(
            form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PROJECT),
            campaignID,
          ),
        );
      }
      setIsSaveCampaignLoading(false);
      setCampaignSubmitMode('');
    }
  };

  useEffect(() => {
    if (isRequestsSuccessful(createCampaignRequest)) {
      handleSuccessfulCampaignRequest();
    }
    if (createCampaignRequest && createCampaignRequest.isFailure) {
      setIsSaveCampaignLoading(false);
      setCampaignSubmitMode('');
    }
  }, [createCampaignRequest]);

  useEffect(() => {
    if (isRequestsSuccessful(updateCampaignRequest)) {
      handleSuccessfulCampaignRequest();
    }
    if (updateCampaignRequest && updateCampaignRequest.isFailure) {
      setIsSaveCampaignLoading(false);
      setCampaignSubmitMode('');
    }
  }, [updateCampaignRequest]);

  useEffect(() => {
    if (
      isRequestsSuccessful(updateCampaignStatusRequest) &&
      campaignSubmitMode === CAMPAIGN_STATE.updateToPendingStatus
    ) {
      snackBar.success({
        content: `You submitted ${form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_NAME)} successfully.`,
      });
      setCampaignSubmitMode('');
      props.router.replace(
        UCD_ROUTES_LINK.SMART_CAMPAIGN_VIEW(
          form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PROJECT),
          newlyCreatedCampaign || (campaignView && campaignView.campaignID),
        ),
      );
    }
  }, [updateCampaignStatusRequest]);

  useEffect(() => {
    handleFormFieldsChange();
  }, [
    primaryLanguageContentsInForm,
    currentContentViewIndex,
    watchSegments,
    shouldUseContentPortalProducts,
    contentPortalProducts,
    hasContentPortalProductsLoaded,
    hasContentPortalProductsLoadFailed,
  ]);

  const contentViews = useMemo(() => {
    return [
      {
        type: VIEW_TYPES.METADATA_INPUT_FORM,
        component: (
          <SmartCampaignCreateEdit
            configs={configs}
            isEditMode={isEditSmartCampaignMode}
          />
        ),
      },
      ...(isPCIFormat
        ? [
          {
            type: VIEW_TYPES.PCI_METADATA_FORM,
            component: <SmartCampaignPCIMetadata />,
          },
        ]
        : []),
      {
        type: VIEW_TYPES.PREVIEW,
        component: <SmartCampaignPreview />,
      },
    ];
  }, [
    configs,
    projectView,
    selectedLanguages,
    callToActionOptions,
    isMystiqueCallPending,
    imageDataForEachLanguage,
    primaryLanguageContentsInForm,
    props.route.path,
    props.params.campaignId,
    isPCIFormat,
  ]);

  const contentComponent = useMemo(() => {
    const view = contentViews[currentContentViewIndex];
    if (!view) {
      return null;
    }

    return view.component;
  }, [contentViews, currentContentViewIndex]);

  const { showNextButton, showPreviousButton, showApprovalButton } =
    useMemo(() => {
      const showNextButton = currentContentViewIndex < contentViews.length - 1;
      const showPreviousButton = currentContentViewIndex > 0;
      const showApprovalButton =
        currentContentViewIndex === contentViews.length - 1;

      return { showNextButton, showPreviousButton, showApprovalButton };
    }, [currentContentViewIndex, contentViews.length]);

  const createOrUpdateCampaign = async (smartCampaignPayload: any) => {
    if (!isEditSmartCampaignMode && !isTaiwanCampaignCreationAllowed(countryId, user.email)) {
      snackBar.error({
        content: TAIWAN_CAMPAIGN_CREATION_RESTRICTED_MESSAGE,
      });
      return;
    }

    const errors = await validateMandatoryFieldsForCreateOrUpdate(
      form,
      shouldUseContentPortalProducts,
      contentPortalProducts,
      hasContentPortalProductsLoaded,
      hasContentPortalProductsLoadFailed,
    );
    // TODO: add scribe events for above two dispatch

    if (errors.length) {
      snackBar.warning({ content: errors[0] });
      return;
    }
    setIsSaveCampaignLoading(true);
    if (
      (isEditSmartCampaignMode && props.params.entityId) ||
      newlyCreatedCampaign
    ) {
      dispatch(
        updateUCDCampaign(
          smartCampaignPayload,
          form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PROJECT),
          props.params.campaignId || newlyCreatedCampaign,
        ),
      );
    } else {
      dispatch(
        createUCDCampaign(
          smartCampaignPayload,
          form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PROJECT),
        ),
      );
    }
  };

  const handleNextBtnClick = async () => {
    // Handle validations.
    if (!showNextButton) {
      return;
    }
    const errors = await validateMandatoryFieldsForCreateOrUpdate(
      form,
      shouldUseContentPortalProducts,
      contentPortalProducts,
      hasContentPortalProductsLoaded,
      hasContentPortalProductsLoadFailed,
    );
    if (errors.length) {
      snackBar.warning({ content: errors[0] });
      return;
    }

    if (showControlGroup && !primaryLanguageContentsInForm.selectedControlGroupVariant) {
      snackBar.error({ content: 'Mark a control group to continue.' });
      return;
    }

    setCurrentContentViewIndex((previousValue) => previousValue + 1);
  };

  const handlePreviousBtnClick = () => {
    if (!showPreviousButton) {
      return;
    }

    setCurrentContentViewIndex((previousValue) => previousValue - 1);
  };

  const handleSaveAsDraft = () => {
    const smartCommsPayload = getSmartCampaignPayload(
      campaignView,
      form,
      projectView,
      user,
      countries,
      selectedLanguages,
      primaryLanguageContentsInForm,
      contentPortalProducts,
      shouldUseContentPortalProducts,
    );
    smartCommsPayload.projectID =
      form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PROJECT) || '';
    setCampaignSubmitMode(CAMPAIGN_STATE.draft);
    createOrUpdateCampaign(smartCommsPayload);
  };

  const handleBackButtonRoutes = () => {
    if (isCreateSmartCampaignMode) {
      props.router.push(UCD_ROUTES_LINK.HOME());
    } else {
      props.router.goBack();
    }
  };

  // handler for browser back.
  const handlePopState = (event: PopStateEvent) => {
    if (!isCreateSmartCampaignMode) {
      return;
    }

    event.preventDefault();
    window.history.pushState({ modalOpened: true }, '', props.route.path);
    if (
      form.isFieldsTouched(Object.values(SMART_CAMPAIGN_FORM_KEYS)) ||
      isContentVariantsTouched
    ) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      handleBackButtonRoutes();
    }
  };

  const handleBackButtonClick = () => {
    if (
      form.isFieldsTouched(Object.values(SMART_CAMPAIGN_FORM_KEYS)) ||
      isContentVariantsTouched
    ) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      handleBackButtonRoutes();
    }
  };

  const handleFormFieldsChange = () => {
    const shouldDisable = !(
      isMetadataInputFormFilled({
        formInstance: form,
        isPrimaryInputFormViewActive: currentContentViewIndex === 0, // primary input form filled view index is '0'
        shouldUseContentPortalProducts,
        contentPortalProducts,
        hasContentPortalProductsLoaded,
        hasContentPortalProductsLoadFailed,
      }) &&
      numberOfSelectedVariants >= MIN_SELECTED_VARIANTS_COUNT_IN_SMART_CAMPAIGN
    );
    setIsNextButtonDisabled(shouldDisable);
  };

  const handleFinish = () => {
    const smartCampaignPayload = getSmartCampaignPayload(
      campaignView,
      form,
      projectView,
      user,
      countries,
      selectedLanguages,
      primaryLanguageContentsInForm,
      contentPortalProducts,
      shouldUseContentPortalProducts,
    );
    smartCampaignPayload.projectID =
      form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PROJECT) || '';
    if (!smartCampaignPayload.campaign.isXCOEnabledByCountry) {
      smartCampaignPayload.campaign.smartCommsMetadata = {};
    }
    smartCampaignPayload.campaign.smartCommsMetadata.xcoEnabled = isXCOEnabled({
      configs,
      campaignView,
      countryID: countryId,
    });

    smartCampaignPayload.campaign.smartCommsMetadata[SMART_CAMPAIGN_FORM_KEYS.IS_RECURRING_CAMPAIGN] = form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.IS_RECURRING_CAMPAIGN);
    smartCampaignPayload.campaign.smartCommsMetadata[SMART_CAMPAIGN_FORM_KEYS.RECURRENCE_WINDOW_DAYS] = form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.RECURRENCE_WINDOW_DAYS) || 0;
    smartCampaignPayload.campaign.smartCommsMetadata[SMART_CAMPAIGN_FORM_KEYS.GMS_ENABLED] = false;

    const errors = validateSmartCommsCampaignPayload(smartCampaignPayload);
    if (Object.keys(errors).length) {
      snackBar.error({ content: errors[Object.keys(errors)[0]] });
      return;
    }
    setCampaignSubmitMode(CAMPAIGN_STATE.approval);
    createOrUpdateCampaign(smartCampaignPayload);
  };
  // 'generatedContent' will be array of {"Header": "", "Message": ""}
  const handleMystiqueResponse = (generatedContents: any[]) => {
    const nextRoundNumber = primaryLanguageContentsInForm.lastRoundNumber + 1;
    const roundName = getSmartCommsRoundNameFromNumber(nextRoundNumber);

    const roundData: any = {};
    const variants = generatedContents.map((content, index) => {
      const variantName = getSmartCommsVariantNameFromNumber(
        primaryLanguageContentsInForm.lastVariantNumber + index + 1,
      );

      const title = content.Header;
      const message = content.Message;
      const titleAndMessage = {
        title: mapAttributesValuesToLabelsInMystiqueResponse(title, false),
        message: mapAttributesValuesToLabelsInMystiqueResponse(message, false),
      };
      const variantId = uuid4();
      const slang = content.slang;
      roundData[variantName] = {
        ...titleAndMessage,
        isChosen: false,
        uuid: variantId,
        slang: content.slang,
      };

      fillContentContainerFormValues({
        roundName,
        variantName,
        formInstance: form,
        content: { ...titleAndMessage, slang },
        language: selectedLanguages[0],
      });
      return {
        id: variantId,
        languages: [
          { language: selectedLanguages[0], payload: titleAndMessage },
        ],
      };
    });

    setPrimaryLanguageContentsInForm((previousContent: PrimaryLanguageContents) => ({
      mode: SMART_CAMPAIGN_CONTENT_SOURCE_MODES.automatic,
      lastRoundNumber: nextRoundNumber,
      lastVariantNumber:
        primaryLanguageContentsInForm.lastVariantNumber +
        generatedContents.length,
      rounds: {
        ...primaryLanguageContentsInForm.rounds,
        [roundName]: roundData,
      },
      selectedControlGroupVariant: previousContent.selectedControlGroupVariant,
    }));
    sendScribeEventForMystiqueContentGeneration({
      variants,
      campaignID,
      campaignView,
      formInstance: form,
      mystiqueRequestTime,
      mystiqueRequestPayload,
      roundNumber: nextRoundNumber,
    });

    if (!isContentVariantsTouched) {
      setIsContentVariantsTouched(true);
    }
  };

  // to create 'contents' for 'smartCommsContentMetadata' in request payload.
  const handleContentVariantsInGetCampaignResponse = () => {
    /**
     * - `isContentEmpty` will be true if: campaignView.campaign.smartCommsContentMetadata.content === undefined
     * - Even though `isContentEmpty` is true, in some cases `callToActionOptions` will be returned all other values will be undefined.
     *
     * NOTE: While duplicating a campaign - campaignView.campaign.smartCommsContentMetadata.content will be undefined, all other values inside campaignView.campaign.smartCommsContentMetadata will be present inside it.
     */
    const {
      rounds,
      lastRoundNumber,
      primaryLanguage,
      lastVariantNumber,
      isContentEmpty,
      callToActionOptions,
      imageDataBasedOnLanguage,
      selectedControlGroupVariant,
      selectedLanguagesInCampaign,
      currentContentSource,
    } = transformContentVariantsInCampaignResponse({
      campaignView,
      formInstance: form,
      contentPortalProducts,
      shouldUseContentPortalProducts,
    });

    if (callToActionOptions) {
      setCallToActionOptions(callToActionOptions);
    }

    if (isContentEmpty) {
      setPrimaryLanguageContentsInForm((prev: PrimaryLanguageContents) => ({
        ...prev,
        mode: currentContentSource,
      }));
      return;
    }

    setImageDataForEachLanguage(imageDataBasedOnLanguage);
    setPrimaryLanguageContentsInForm((prev: PrimaryLanguageContents) => ({
      ...prev,
      mode: currentContentSource,
      rounds,
      lastRoundNumber,
      lastVariantNumber,
      selectedControlGroupVariant,
    }));

    setSelectedLanguages(
      _.union([primaryLanguage], selectedLanguagesInCampaign), // force primaryLanguage to be the first.
    );
  };

  useEffect(() => {
    const currentCampaignID = props.params.campaignId || campaignToDuplicateId;
    if (!campaignView || currentCampaignID !== campaignView.campaignID) {
      return;
    }

    if (
      shouldUseContentPortalProducts &&
      countryCode &&
      _.isEmpty(contentPortalProducts) &&
      !contentPortalProductsRequest.isSuccessful &&
      !contentPortalProductsRequest.isFailure
    ) {
      return;
    }

    handleContentVariantsInGetCampaignResponse();
  }, [
    campaignView,
    props.params,
    shouldUseContentPortalProducts,
    countryCode,
    contentPortalProducts,
    contentPortalProductsRequest,
  ]);

  const isSaveAsDraftLoading = useMemo(() => {
    return (
      campaignSubmitMode !== CAMPAIGN_STATE.approval &&
      campaignSubmitMode !== CAMPAIGN_STATE.updateToPendingStatus &&
      isSaveCampaignLoading
    );
  }, [isSaveCampaignLoading, campaignSubmitMode]);

  const isSubmitForApproverLoading = useMemo(() => {
    return (
      (campaignSubmitMode === CAMPAIGN_STATE.approval ||
        campaignSubmitMode === CAMPAIGN_STATE.updateToPendingStatus) &&
      isSaveCampaignLoading
    );
  }, [isSaveCampaignLoading, campaignSubmitMode]);

  const getPendingFieldsTooltip = () => {
    if (!isNextButtonDisabled) {
      return null;
    }

    const formInstance = form;
    const isPrimaryInputFormViewActive = currentContentViewIndex === 0;
    const minVariantsRequired = MIN_SELECTED_VARIANTS_COUNT_IN_SMART_CAMPAIGN;

    const fieldsStatus = getRequiredFieldsStatus({
      formInstance,
      isPrimaryInputFormViewActive,
      numberOfSelectedVariants,
      minVariantsRequired,
      shouldUseContentPortalProducts,
      contentPortalProducts,
      hasContentPortalProductsLoaded,
      hasContentPortalProductsLoadFailed,
    });

    if (fieldsStatus.length === 0) {
      return null;
    }

    return (
      <div>
        {fieldsStatus.map((field, index) => (
          <div key={index}>{field.isDone ? '✅' : '⚠️'} {field.label}</div>
        ))}
      </div>
    );
  };

  // Helper function to process content variants
  const constructContentVariants = (contents: any[]) => {
    return contents.map((content: any) => ({
      ...content,
      rounds: content.rounds.map((round: any) => ({
        ...round,
        variants: round.variants.map((variant: any) => ({
          ...variant,
          id: uuid4(), // Use uuid4() instead of custom UUID generation
          hedwigTemplateID: undefined, // Remove hedwigTemplateID
        })),
      })),
    }));
  };

  if (isCampaignLoading) {
    return (
      <Flex align="center" justify="center" style={{ height: '100%' }}>
        <Spin />
      </Flex>
    );
  }

  return (
    <div
      className={'ucd-basic'}
      style={{ backgroundColor: colorBackgroundAlt }}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={CustomizeRequiredMark}
        onFinish={handleFinish}
        onFieldsChange={handleFormFieldsChange}
      >
        {/* Header */}
        <Flex
          align="center"
          justify="space-between"
          style={{
            top: 0,
            zIndex: 7,
            padding: 16,
            width: '100%',
            position: 'sticky',
            backgroundColor: colorBaseNeutralWhite,
          }}
        >
          <Flex align="center" justify="center" gap={8}>
            <Button
              type="text"
              onClick={handleBackButtonClick}
              style={{ padding: 10 }}
            >
              <ArrowLeftUniversal
                className="back-arrow"
                style={{ fontSize: 20 }}
              />
            </Button>
            <Typography.Title
              style={{ display: 'contents', color: colorBackgroundAltInverse }}
              level={4}
            >
              {form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_NAME) ||
                'Untitled Campaign'}
            </Typography.Title>
          </Flex>
          <Flex align="center" justify="center" gap={8}>
            <Button
              type="secondary"
              onClick={handleSaveAsDraft}
              loading={isSaveAsDraftLoading}
            >
              Save as draft
            </Button>
            {showPreviousButton && (
              <Button onClick={handlePreviousBtnClick}>Previous</Button>
            )}
            {showNextButton && (
              <Tooltip title={getPendingFieldsTooltip()} placement="bottomRight">
                <span>
                  <Button
                    type="primary"
                    onClick={handleNextBtnClick}
                    disabled={isNextButtonDisabled}
                  >
                    {isNextButtonDisabled ? '⚠️' : '✅'} Next
                  </Button>
                </span>
              </Tooltip>
            )}

            {showApprovalButton && (
              <Button
                type="primary"
                htmlType="submit"
                loading={isSubmitForApproverLoading}
              >
                Submit for approval
              </Button>
            )}
          </Flex>
        </Flex>
        <Divider style={{ margin: 0 }} />
        {/* Content */}
        {contentComponent}
      </Form>
      <SmartCommsUnsavedChangesConfirmationModal
        isOpen={isUnsavedChangesModalOpen}
        isCreateSmartCampaignMode={
          isCreateSmartCampaignMode || isDuplicateSmartCampaignMode
        }
        onSaveAsDraft={handleSaveAsDraft}
        onClose={() => {
          setIsUnsavedChangesModalOpen(false);
        }}
        onDelete={handleBackButtonRoutes}
      />
    </div>
  );
};

export default UCDCreateSmartCommsCampaign;
